import React, { useEffect } from "react";
import * as z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useMixpanel } from "@/hooks/useMixpanel";

interface DomainSuggestionPopupProps {
  domain: string;
  onConfirm: (domain: string) => void;
  onCancel: () => void;
}

const FormSchema = z.object({
  domain: z.string().min(2, { message: "Please select your email domain" }),
});

const EmailDomainPopup: React.FC<DomainSuggestionPopupProps> = ({
  domain,
  onConfirm,
  onCancel,
}) => {
  const { trackEvent } = useMixpanel();

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      domain: "",
    },
  });

  const handleEmailDomainSubmit = (values: z.infer<typeof FormSchema>) => {
    onConfirm(values.domain);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    form.handleSubmit(handleEmailDomainSubmit)();
  };

  useEffect(() => {
    trackEvent("Signup: Email Domain Modal Page View", {
      page: "Signup",
    });
  }, [trackEvent]);

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          variant="link"
          className="-mb-2 px-0"
          onClick={() => {
            trackEvent("Signup: Email Domain Modal Opened", {
              page: "Signup: Email Domain Modal",
              domain,
            });
          }}
        >
          {`Did you mean '${domain}' ?`}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="text-left">
          <DialogTitle>Check your email domain</DialogTitle>
          <DialogDescription>
            It looks like you might have misspelled the domain in your email address.
          </DialogDescription>
        </DialogHeader>
        {/* <div className="space-y-2">
          <Form {...form}>
            <form onSubmit={handleSubmit} className="space-y-6">
              <FormField
                name="domain"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormDescription>
                      Here are some common domains that you might have intended to use:
                    </FormDescription>
                    <FormControl>
                      <ul className="flex flex-wrap items-center justify-start gap-2">
                        {COMMON_EMAIL_DOMAINS.map((suggestedDomain) => (
                          <li key={suggestedDomain} className="cursor-pointer">
                            <Badge
                              variant="outline"
                              className={
                                form.getValues("domain") === suggestedDomain
                                  ? "outline outline-2 outline-primary drop-shadow-lg"
                                  : ""
                              }
                              onClick={() => form.setValue("domain", suggestedDomain)}
                            >
                              {suggestedDomain}
                            </Badge>
                          </li>
                        ))}
                      </ul>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <DialogFooter className="sm:justify-end">
                <div className="space-x-2">
                  <Button type="button" variant="outline" onClick={onCancel}>
                    No, it's fine
                  </Button>
                  <Button type="submit">Yes, fix it</Button>
                </div>
              </DialogFooter>
            </form>
          </Form>
        </div> */}

        <div className="space-y-4">
          <p className="text-base font-medium text-foreground">
            Did you mean <span className="font-semibold">'{domain}'</span> ?
          </p>
          <DialogFooter className="sm:justify-end">
            <div className="space-x-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  trackEvent("Signup: Email Domain Modal No Button Clicked", {
                    page: "Signup",
                    domain,
                  });
                  onCancel();
                }}
              >
                No, it's fine
              </Button>
              <Button
                type="button"
                onClick={() => {
                  trackEvent("Signup: Email Domain Modal Yes Button Clicked", {
                    page: "Signup",
                    domain,
                  });
                  onConfirm(domain);
                }}
              >
                Yes
              </Button>
            </div>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EmailDomainPopup;
